import {useEffect, useState} from 'react';

import {TextField, Box, DialogContent, DialogActions} from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import dayjs from 'dayjs';

import {REGULAR_EXPRESSIONS, REGULAR_EXPRESSIONS_ERROR_TEXTS} from '../../../../const';
import useBudget from '../../../../hooks/providers/useBudget';
import useReport from '../../../../hooks/providers/useReport';
import {getPossibleBudgetYears} from '../../../../utils';
import Button from '../../../form/buttons/Button/Button';
import ErrorMessage from '../../../form/ErrorMessage/ErrorMessage';
import FormSelect from '../../../form/MuiSelect/MuiSelect';
import BaseModal from '../../../modal/BaseModal';

// eslint-disable-next-line complexity
const CreateOrUpdateBudgetDialog = () => {
  const {selectedProject: project} = useReport();
  const {
    createBudgetError,
    setCreateBudgetError,
    isCreateBudgetForm: isCreation,
    createOrUpdateBudget,
    setIsCreateOrUpdateBudgetFormOpen,
    isCreateOrUpdateBudgetFormOpen: isOpen,
    selectedBudget: editedBudgetName,
    selectedBudgetYears,
    alreadyTakenBudgetNames
  } = useBudget();

  console.log({isOpen});

  const [budgetName, setBudgetName] = useState('');
  const [year, setYear] = useState('');
  const [referenceYear, setReferenceYear] = useState('');

  const alreadyExistingBudgetYears = isCreation ? [] : selectedBudgetYears;
  const possibleBudgetYears = getPossibleBudgetYears(alreadyExistingBudgetYears);

  const title = isCreation ? 'Créer un budget' : 'Ajouter une année';
  const submitButtonText = isCreation ? 'Créer le budget' : "Ajouter l'année";
  const budgetNameInputLabel = isCreation ? 'Renseignez le nom du budget à créer...' : 'Nom du budget';

  const budgetRegex = REGULAR_EXPRESSIONS.labelWithMinimumChars(4);
  const budgetNameAlreadyTaken = !!(isCreation && alreadyTakenBudgetNames.includes(budgetName));
  const hasLabelError = budgetName !== '' && !budgetRegex.test(budgetName);
  const canSubmit = budgetName && year && referenceYear && !hasLabelError && !budgetNameAlreadyTaken;

  const getBudgetNameErrorText = () => {
    if (hasLabelError) {
      return REGULAR_EXPRESSIONS_ERROR_TEXTS.labelWithMinimumChars(4);
    }
    if (budgetNameAlreadyTaken) {
      return 'Ce nom de budget est déjà utilisé';
    }
    return '';
  };

  useEffect(() => {
    setCreateBudgetError('');
    // If we are updating an existing budget, we have to automatically set the budget name
    if (!isCreation) {
      setBudgetName(editedBudgetName);
    }

    // Clean data on modal close
    if (!isOpen) {
      setBudgetName('');
      setYear('');
      setReferenceYear('');
    }
  }, [isOpen, isCreation]);

  const sortedFiscalYears = (project?.fy_list || []).sort((a, b) => {
    const dateA = dayjs(a, 'DD-MM-YYYY');
    const dateB = dayjs(b, 'DD-MM-YYYY');
    return dateA - dateB;
  });

  const onClose = () => {
    setIsCreateOrUpdateBudgetFormOpen(false);
  };

  return (
    <BaseModal onClose={onClose} open={isOpen} title={title}>
      <DialogContent>
        <Box mt={1} mb={2}>
          <TextField
            disabled={!isCreation}
            sx={{mb: 2}}
            value={budgetName}
            onChange={e => setBudgetName(e.target.value)}
            fullWidth
            label={budgetNameInputLabel}
            defaultValue=""
            error={hasLabelError || budgetNameAlreadyTaken}
            helperText={getBudgetNameErrorText()}
          />

          <FormSelect
            sx={{width: '100%', mb: 2}}
            label="Sélectionner une année à budgéter"
            labelId="year-label"
            value={year}
            onChange={e => {
              setYear(e.target.value);
            }}
            items={possibleBudgetYears.map(y => (
              <MenuItem key={y} value={y}>
                {y}
              </MenuItem>
            ))}
          />

          <FormSelect
            sx={{width: '100%', mb: 2}}
            label="Sélectionner une année de référence"
            labelId="reference-year-label"
            value={referenceYear}
            onChange={e => {
              setReferenceYear(e.target.value);
            }}
            items={sortedFiscalYears.map(fy => (
              <MenuItem key={fy} value={fy}>
                {fy}
              </MenuItem>
            ))}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{justifyContent: 'center'}}>
        <Button
          disabled={!canSubmit}
          variant="contained"
          color="primary"
          onClick={() => {
            createOrUpdateBudget({
              budgetName: budgetName.trim(),
              budgetYear: year,
              budgetReferenceYear: referenceYear,
              siren: project?.siren
            });
            onClose();
          }}
        >
          {submitButtonText}
        </Button>
      </DialogActions>
      {createBudgetError !== '' && (
        <Box px={3} pb={2}>
          <ErrorMessage message={createBudgetError} />
        </Box>
      )}
    </BaseModal>
  );
};

export default CreateOrUpdateBudgetDialog;
